// components/chat/PredefinedPromptsDropdown.js
import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { apiClient } from "../../services/apiClient";
import clsx from "clsx";

const PredefinedPromptsDropdown = ({ onSelectPrompt, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [promptsByCategory, setPromptsByCategory] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Load prompts on mount
  useEffect(() => {
    fetchPrompts();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.getPredefinedPrompts();

      if (response.success) {
        setPrompts(response.data.prompts);
        setPromptsByCategory(response.data.promptsByCategory);
      } else {
        throw new Error(response.message || "Failed to load prompts");
      }
    } catch (error) {
      console.error("Error fetching predefined prompts:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    setSearchTerm(""); // Reset search when toggling
  };

  const handleSelectPrompt = (prompt) => {
    onSelectPrompt?.(prompt);
    setIsOpen(false);
    setSearchTerm("");
  };

  const getFilteredPrompts = () => {
    if (!searchTerm.trim()) {
      return promptsByCategory;
    }

    const filtered = {};
    const searchLower = searchTerm.toLowerCase();

    Object.keys(promptsByCategory).forEach((category) => {
      const categoryPrompts = promptsByCategory[category].filter(
        (prompt) =>
          prompt.prompt_name.toLowerCase().includes(searchLower) ||
          prompt.prompt_text.toLowerCase().includes(searchLower) ||
          prompt.description?.toLowerCase().includes(searchLower)
      );

      if (categoryPrompts.length > 0) {
        filtered[category] = categoryPrompts;
      }
    });

    return filtered;
  };

  const filteredPromptsByCategory = getFilteredPrompts();
  const hasResults = Object.keys(filteredPromptsByCategory).length > 0;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Header Button - Matches existing header button style */}
      <button
        onClick={handleToggleDropdown}
        disabled={disabled || loading}
        className={clsx(
          "p-2 rounded-lg transition-colors duration-200 text-sm",
          {
            "hover:bg-slate-200 text-slate-600": !isOpen && !disabled,
            "bg-primary-100 text-primary-600": isOpen,
            "bg-slate-100 text-slate-400 cursor-not-allowed":
              disabled || loading,
          }
        )}
        title={loading ? "Loading prompts..." : "Quick Prompts"}
      >
        Predefined Prompts{" "}
        <FontAwesomeIcon
          icon={loading ? "spinner" : "magic"}
          className={clsx({
            "animate-spin": loading,
          })}
        />
      </button>

      {/* Dropdown Menu - Positioned to stay on screen */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-96 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-slate-200">
            <div className="relative">
              <FontAwesomeIcon
                icon="search"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 text-sm"
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search prompts..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Content */}
          <div className="max-h-64 overflow-y-auto">
            {error ? (
              <div className="p-4 text-center">
                <FontAwesomeIcon
                  icon="exclamation-triangle"
                  className="text-red-500 mb-2"
                />
                <p className="text-sm text-red-600">{error}</p>
                <button
                  onClick={fetchPrompts}
                  className="mt-2 text-xs text-primary-600 hover:text-primary-800"
                >
                  Try again
                </button>
              </div>
            ) : !hasResults ? (
              <div className="p-4 text-center">
                <FontAwesomeIcon
                  icon="search"
                  className="text-slate-300 mb-2"
                />
                <p className="text-sm text-slate-500">
                  {searchTerm ? "No prompts found" : "No prompts available"}
                </p>
              </div>
            ) : (
              Object.keys(filteredPromptsByCategory).map((category) => (
                <div key={category}>
                  {/* Category Header */}
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
                    <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      {category}
                    </h3>
                  </div>

                  {/* Category Prompts */}
                  <div className="py-1">
                    {filteredPromptsByCategory[category].map((prompt) => (
                      <button
                        key={prompt.prompt_id}
                        onClick={() => handleSelectPrompt(prompt)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors duration-150"
                      >
                        <div className="flex items-start space-x-2">
                          <FontAwesomeIcon
                            icon="magic"
                            className="text-primary-500 text-sm flex-shrink-0 mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {prompt.prompt_name}
                            </p>
                            {prompt.description && (
                              <p className="text-xs text-slate-600 truncate mt-0.5">
                                {prompt.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {hasResults && (
            <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 text-center">
              <p className="text-xs text-slate-500">
                Click a prompt to add it to your message
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PredefinedPromptsDropdown;
